import { Link } from 'react-router-dom';
import { usePlansCatalog } from '@/context/PlansContext';
import {
  checkoutHref,
  effectivePlanPriceCents,
  formatPlanChannels,
  formatPlanPrice,
  formatPlanSeats,
  isCustomQuotePlan,
  type PublicPlan,
} from '@/content/plans';
import { buyablePlans } from '@/lib/calculator';
import { supportWhatsAppHref } from '@/lib/supportChat';

const TAGLINES: Record<string, string> = {
  start: 'Para começar a atender com organização',
  pro: 'Para a equipe comercial que cresce',
  business: 'Para várias filas e setores',
  scale: 'Para operação com muitos atendentes',
};

function PlanCard({ plan }: { plan: PublicPlan }) {
  const cents = effectivePlanPriceCents(plan);
  const [reais, centavos] = formatPlanPrice(cents).replace('R$', '').trim().split(',');
  const perUser = plan.max_users ? Math.round(cents / 100 / plan.max_users) : null;
  const pop = plan.is_popular;
  return (
    <div className={`hx-pcard rev${pop ? ' pop' : ''}`}>
      <div>
        <div className="hx-pname">
          {plan.name}
          {pop && <em>Mais popular</em>}
        </div>
        <div className="tagline">{TAGLINES[plan.code] ?? 'Assinatura mensal, sem fidelidade'}</div>
      </div>
      <div>
        <div className="hx-amt">
          <span>R$</span>
          <b>
            {reais},{centavos}
          </b>
          <span>/mês</span>
        </div>
        {perUser && <div className="hx-per">≈ R$ {perUser} por usuário</div>}
      </div>
      <Link className={`btn hx-pbtn ${pop ? 'hx-btn-main' : 'line'}`} to={checkoutHref(plan.code)}>
        {pop ? `Assinar ${plan.name} agora` : `Assinar ${plan.name}`}
      </Link>
      <ul className="hx-plist">
        <li>{formatPlanSeats(plan)} no atendimento</li>
        <li>{formatPlanChannels(plan)} de WhatsApp</li>
      </ul>
    </div>
  );
}

export function HomePricing({ freeApps }: { freeApps: number }) {
  const { sortedPlans } = usePlansCatalog();
  const plans = buyablePlans(sortedPlans);
  const enterprise = sortedPlans.find(isCustomQuotePlan);
  const ref = plans[0];
  return (
    <section className="hx-sec soft" id="precos">
      <div className="hx-wrap">
        <div className="hx-head rev">
          <span className="hx-label blue">Preços</span>
          <h2>Quatro planos. Um preço por equipe, sem taxa por mensagem.</h2>
          <p>
            O plano traz atendimento e funil. A Meta cobra as mensagens de modelo direto na conta da sua empresa,
            sem markup da Operax.
          </p>
        </div>
        <div className="hx-price-grid">
          {plans.map((p) => (
            <PlanCard key={p.code} plan={p} />
          ))}
        </div>
        <div className="hx-two">
          <div className="hx-note rev">
            <h3>Em todos os planos</h3>
            <ul className="hx-checks">
              <li>WhatsApp oficial da Meta</li>
              <li>Inbox com filas e histórico</li>
              <li>Funil de vendas na conversa</li>
              <li>{freeApps} apps para ativar sem custo, como PDV, Pix e Lembretes</li>
            </ul>
            <a className="hx-link" href="#apps">
              Ver todos os apps, grátis e pagos →
            </a>
          </div>
          <div className="hx-note soft rev">
            <h3>Enterprise</h3>
            <p>Usuários e números sob medida para redes, franquias e hospitais.</p>
            <a className="btn hx-mini" href={enterprise ? '/solicitar-orcamento' : supportWhatsAppHref()}>
              Falar com vendas
            </a>
            {ref && (
              <p style={{ fontSize: '.85rem', color: '#64748b' }}>
                {ref.extra_seat_monthly_cents ? `Extra em qualquer plano: usuário ${formatPlanPrice(ref.extra_seat_monthly_cents)}/mês` : ''}
                {ref.extra_connection_monthly_cents ? ` · número ${formatPlanPrice(ref.extra_connection_monthly_cents)}/mês.` : ''}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
